// Platform detection, used only to decide which app store to show first.
//
// Three things this deliberately does NOT do:
//
// 1. Redirect. User-agent sniffing is wrong often enough — desktop mode on
//    a phone, an in-app browser, a spoofed string — that sending someone
//    to the wrong store with no way back is worse than one extra tap. Both
//    stores are always reachable; detection only changes the order.
//
// 2. Run during render. Detection needs `navigator`, which doesn't exist
//    when the page is prerendered. Reading it during the first render
//    would make the server HTML and the hydrated HTML disagree. So this
//    returns "unknown" until an effect runs, and components show a neutral
//    state until then.
//
// 3. Trust the platform string alone for iPad. iPadOS 13+ reports itself
//    as Macintosh, so an iPad falls through to desktop unless you also
//    check for touch support — which is the check below.

export function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";

  // iPadOS 13+ masquerading as macOS.
  if (/Mac/.test(platform) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1) {
    return "ios";
  }

  return "desktop";
}

/**
 * True when the page is running somewhere a camera and motion sensors are
 * plausibly available AND the context allows them. getUserMedia and the
 * motion APIs both require a secure context, so an http:// dev server on a
 * phone will fail regardless of hardware — worth detecting up front rather
 * than letting a permission prompt fail confusingly.
 */
export function canMeasure() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const secure = window.isSecureContext || window.location.hostname === "localhost";
  const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  return secure && hasCamera;
}

export function hasMotionSensors() {
  return typeof window !== "undefined" && typeof window.DeviceOrientationEvent !== "undefined";
}

/**
 * iOS 13+ requires an explicit permission request for motion and
 * orientation, and it must be called from inside a user gesture. Android
 * grants it without asking. Returning "granted" where no request function
 * exists keeps callers from having to special-case that.
 */
export async function requestMotionPermission() {
  if (typeof DeviceOrientationEvent === "undefined") return "unsupported";
  if (typeof DeviceOrientationEvent.requestPermission !== "function") return "granted";
  try {
    return await DeviceOrientationEvent.requestPermission();
  } catch (e) {
    return "denied";
  }
}
